/**
 * PO page button affordance — v1.27 (approved mockup po-hierarchy-v2.html).
 * Maps onto fi-design-standards; see affordance-style-guide.md for per-button table.
 */
export const PO_COLORS = {
  green: '#1E7A3A',
  greenHover: '#166030',
  orange: '#FF6A13',
  orangeHover: '#F35627',
  // v1.27.19 — softened ~20% from #513FAD so green status verbs keep the
  // strongest contrast; hover steps back to the old base.
  purple: '#6C5AC4',
  purpleHover: '#513FAD',
  blueText: '#1D4ED8',
  blueBorder: '#BFDBFE',
  blueTint: '#EFF6FF',
  blueTintHover: '#DBEAFE',
  ghostBorder: '#9BB4DC',
  ghostBorderHover: '#6B9AD4',
  chipBorder: '#F5DCC5',
  chipTint: '#FFF4EA',
  tableHeaderBg: '#F0F0F2',
  tableHeaderBorder: '#D8D8DC',
  tableHeaderText: '#3d3d46',
  supCodeHeader: '#6a1b9a',
};

/** L1 — status primary (32px): advance/confirm verbs — ONE per region */
export const BTN_STATUS_GREEN = {
  textTransform: 'none' as const, fontSize: '13px', fontWeight: 800,
  height: 32, px: 2.25, boxShadow: 'none', borderRadius: '8px',
  backgroundColor: PO_COLORS.green, color: '#fff',
  '&:hover': { backgroundColor: PO_COLORS.greenHover, boxShadow: 'none' },
};

/** L1 attention — confirm goods received etc. */
export const BTN_STATUS_ORANGE = {
  ...BTN_STATUS_GREEN,
  backgroundColor: PO_COLORS.orange,
  '&:hover': { backgroundColor: PO_COLORS.orangeHover, boxShadow: 'none' },
};

/** Purple secondary — Reconcile Supplier Invoice (brand workflow event) */
export const BTN_PURPLE = {
  textTransform: 'none' as const, fontSize: '12.5px', fontWeight: 700,
  height: 28, px: 1.75, boxShadow: 'none', borderRadius: '7px',
  backgroundColor: PO_COLORS.purple, color: '#fff',
  '&:hover': { backgroundColor: PO_COLORS.purpleHover, boxShadow: 'none' },
};

/** L2 — standard working button (28px): line tools, companions, Close */
export const BTN_STANDARD = {
  textTransform: 'none' as const, fontSize: '12.5px', fontWeight: 700,
  height: 28, px: 1.5, boxShadow: 'none', borderRadius: '7px',
  backgroundColor: PO_COLORS.blueTint, color: PO_COLORS.blueText,
  border: `1px solid ${PO_COLORS.blueBorder}`,
  '&:hover': { backgroundColor: PO_COLORS.blueTintHover, borderColor: '#93C5FD' },
};

/** L2b — ghost document tool (28px): footer utility row, icon + label */
export const BTN_GHOST_DOC = {
  textTransform: 'none' as const, fontSize: '12.5px', fontWeight: 600,
  height: 28, px: 1.4, boxShadow: '0 1px 0 rgba(29, 78, 216, 0.04)', borderRadius: '7px',
  backgroundColor: '#FFFFFF', color: PO_COLORS.blueText,
  border: `1px solid ${PO_COLORS.ghostBorder}`,
  '&:hover': { backgroundColor: PO_COLORS.blueTint, borderColor: PO_COLORS.ghostBorderHover, fontWeight: 700 },
  '& .MuiButton-startIcon': { marginRight: '6px', '& > *': { fontSize: 14 } },
};

/** L3 — inline action link */
export const LINK_BLUE = {
  textTransform: 'none' as const, fontSize: '12.5px', fontWeight: 700,
  color: '#2563EB', minWidth: 0, p: 0,
  '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' },
};

/** L4 — quiet + destructive */
export const BTN_GHOST_DANGER = {
  textTransform: 'none' as const, fontSize: '12.5px', fontWeight: 700,
  height: 28, px: 1.5, boxShadow: 'none', borderRadius: '7px',
  backgroundColor: 'transparent', color: '#c62828',
  '&:hover': { backgroundColor: '#fdecea' },
};

/** Choose file — attach zone (light blue standard) */
export const BTN_CHOOSE_ATTACH = {
  ...BTN_STANDARD, flexShrink: 0,
};

/** Choose file — AI zone (purple + sparkle) */
export const BTN_CHOOSE_AI = {
  textTransform: 'none' as const, fontSize: '12.5px', fontWeight: 700,
  height: 28, px: 1.75, boxShadow: 'none', borderRadius: '7px', flexShrink: 0,
  backgroundColor: PO_COLORS.purple, color: '#fff',
  display: 'inline-flex', alignItems: 'center', gap: '7px',
  '&:hover': { backgroundColor: PO_COLORS.purpleHover, boxShadow: 'none' },
};

/** View/Edit + Expand/Collapse chip pair */
export const CHIP_SEG_SX = {
  '& .MuiToggleButton-root': {
    textTransform: 'none', fontSize: '0.72rem', fontWeight: 700,
    py: 0.25, px: 1.4, color: '#6F6489',
    borderColor: PO_COLORS.chipBorder, backgroundColor: PO_COLORS.chipTint,
    '&:hover': { backgroundColor: PO_COLORS.chipTint, color: PO_COLORS.orange },
    '&.Mui-selected': {
      backgroundColor: PO_COLORS.orange, color: '#fff', borderColor: PO_COLORS.orange,
      '&:hover': { backgroundColor: PO_COLORS.orangeHover },
    },
    '&:first-of-type': { borderRadius: '999px 0 0 999px' },
    '&:last-of-type': { borderRadius: '0 999px 999px 0' },
  },
};

/** Line table header band */
export const TABLE_HEAD_CELL = {
  backgroundColor: PO_COLORS.tableHeaderBg, color: PO_COLORS.tableHeaderText,
  fontSize: '11.5px', fontWeight: 700, py: 1.1,
  borderTop: `1px solid ${PO_COLORS.tableHeaderBorder}`,
  borderBottom: `1px solid ${PO_COLORS.tableHeaderBorder}`,
  whiteSpace: 'nowrap' as const,
};

export const TABLE_HEAD_SUPPLIER = {
  ...TABLE_HEAD_CELL, color: PO_COLORS.supCodeHeader, fontWeight: 800,
};

/** Sticky lifecycle footer row — Close · primary · flex · Delete (Draft) */
export const PO_STICKY_LIFECYCLE_SX = {
  position: 'sticky' as const,
  bottom: 0,
  zIndex: 30,
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  flexWrap: 'nowrap' as const,
  width: '100%',
  boxSizing: 'border-box' as const,
  backgroundColor: '#fff',
  mx: -4,
  px: 4,
  pt: 1.5,
  pb: 1.5,
  mt: 2,
  borderTop: '1px dashed #e0e0e0',
  boxShadow: '0 -8px 20px rgba(26, 18, 48, 0.07)',
};

/** Pushes Delete (or other trailing actions) to the far right of the lifecycle row */
export const PO_STICKY_LIFECYCLE_PUSH = { flex: 1, minWidth: 16 } as const;

/** Wrapper so destructive actions never shrink off-screen on narrow viewports */
export const PO_STICKY_LIFECYCLE_TRAIL = { flexShrink: 0, ml: 'auto' } as const;
